import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { compositionPageDimensionsMm } from '../../../composition/geometry';
import { exportStaticMap } from '../../../composition/export/staticExport';
import {
  StaticExportError,
  type StaticExportDpi,
  type StaticExportFormat,
} from '../../../composition/export/model';
import { buildStaticCompositionPlan } from '../../../composition/export/planning';
import {
  buildVectorPdfPlan,
  assertVectorPdfSupported,
} from '../../../composition/export/vectorPdf/planning';
import {
  assertStaticMapFrameSupported,
  getStaticWebGlCapabilities,
} from '../../../composition/export/webGlCapabilities';
import { DialogShell } from '../../../components/DialogShell';
import { useCapability } from '../../../features/useCapability';
import { useAppStore } from '../../state/useAppStore';
import {
  OperationCancelledError,
  type LongOperationState,
} from '../../../operations/longOperation';
import type { StaticExportStageId } from '../../../composition/export/staticExport';

interface ExportMapDialogProps {
  readonly onClose: () => void;
}

const RESOLUTION_LABELS: Readonly<Record<StaticExportDpi, string>> = {
  150: 'Standard — 150 DPI',
  300: 'Print — 300 DPI',
  450: 'High resolution — 450 DPI',
  600: 'Maximum resolution — 600 DPI',
};

const PAGE_LABELS = {
  a5: 'A5',
  a4: 'A4',
  a3: 'A3',
  letter: 'US Letter',
  custom: 'Custom',
} as const;

export function ExportMapDialog({ onClose }: ExportMapDialogProps) {
  const project = useAppStore((state) => state.project);
  const entitlement = useAppStore((state) => state.entitlement);
  const mapLogicalPixelsPerMm = useAppStore((state) => state.shell.compositionPreviewPixelsPerMm);
  const canExport = useCapability('export.static');
  const [format, setFormat] = useState<StaticExportFormat>('png');
  const [dpi, setDpi] = useState<StaticExportDpi>(300);
  const [operation, setOperation] = useState<LongOperationState<StaticExportStageId>>({
    operationId: 0,
    phase: 'idle',
  });
  const [error, setError] = useState<string | null>(null);
  const formatRef = useRef<HTMLSelectElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const operationIdRef = useRef(0);
  const titleId = useId();
  const dimensions = compositionPageDimensionsMm(project.composition.page);
  const planResult = useMemo(() => {
    try {
      if (format === 'pdf-vector') {
        const vector = buildVectorPdfPlan(project, dpi, mapLogicalPixelsPerMm);
        assertVectorPdfSupported(vector, getStaticWebGlCapabilities());
        return { ok: true as const, plan: vector.page };
      }
      const plan = buildStaticCompositionPlan(
        project.composition,
        project.sources,
        project.layers,
        dpi,
        'raster',
        undefined,
        mapLogicalPixelsPerMm,
        project.annotationLayers,
      );
      assertStaticMapFrameSupported(plan, getStaticWebGlCapabilities());
      return {
        ok: true as const,
        plan,
      };
    } catch (cause) {
      return {
        ok: false as const,
        message:
          cause instanceof Error
            ? cause.message
            : 'This page cannot be exported at the selected resolution.',
      };
    }
  }, [dpi, format, mapLogicalPixelsPerMm, project]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const planError = error ?? (!planResult.ok ? planResult.message : null);
  const preparing = ['starting', 'running', 'cancelling'].includes(operation.phase);
  const disabled = preparing || !canExport || !planResult.ok;
  const page = project.composition.page;

  return (
    <DialogShell
      className="new-map-dialog export-data-dialog export-map-dialog"
      closeOnEscape={!preparing}
      initialFocusRef={formatRef}
      onClose={onClose}
      titleId={titleId}
    >
      <h2 id={titleId}>Export map</h2>
      <label className="export-data-control">
        <span>Format</span>
        <select
          aria-label="Export format"
          disabled={preparing}
          onChange={(event) => setFormat(event.target.value as StaticExportFormat)}
          ref={formatRef}
          value={format}
        >
          <option value="png">PNG</option>
          <option value="pdf-vector">PDF — Vector</option>
          <option value="pdf">PDF — Raster</option>
        </select>
      </label>
      <label className="export-data-control">
        <span>{format === 'pdf-vector' ? 'Raster detail' : 'Resolution'}</span>
        <select
          aria-label={format === 'pdf-vector' ? 'Raster detail' : 'Export resolution'}
          disabled={preparing}
          onChange={(event) => setDpi(Number(event.target.value) as StaticExportDpi)}
          value={dpi}
        >
          {([150, 300, 450, 600] as const).map((value) => (
            <option key={value} value={value}>
              {RESOLUTION_LABELS[value]}
            </option>
          ))}
        </select>
      </label>
      {format === 'pdf-vector' ? (
        <p className="export-map-info">
          Vector map layers and page elements stay sharp at any zoom. Basemaps and collision-managed
          map labels use the selected raster detail.
        </p>
      ) : null}
      <div className="export-map-summary">
        <div>
          <span>Page</span>
          <strong>
            {PAGE_LABELS[page.preset]} {page.preset === 'custom' ? '' : page.orientation} ·{' '}
            {dimensions.width} × {dimensions.height} mm
          </strong>
        </div>
        <div>
          <span>{format === 'pdf-vector' ? 'Raster detail' : 'Output'}</span>
          <strong>
            {format === 'pdf-vector'
              ? `${dpi} DPI`
              : planResult.ok
                ? `${planResult.plan.page.widthPx} × ${planResult.plan.page.heightPx} px`
                : 'Unavailable'}
          </strong>
        </div>
      </div>
      {planError ? (
        <p className="style-notice" role="alert">
          {planError}
        </p>
      ) : null}
      {operation.stage || operation.phase === 'cancelling' || operation.phase === 'complete' ? (
        <p aria-live="polite" className="export-operation-status" role="status">
          {operation.phase === 'cancelling'
            ? 'Cancelling export…'
            : operation.phase === 'complete'
              ? 'Export cancelled.'
              : operation.stage?.label}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button
          disabled={operation.phase === 'cancelling'}
          onClick={() => {
            if (preparing) {
              setOperation((current) => ({ ...current, phase: 'cancelling' }));
              abortRef.current?.abort();
            } else onClose();
          }}
          type="button"
        >
          {preparing ? 'Cancel export' : 'Cancel'}
        </button>
        <button
          className="primary-action"
          disabled={disabled}
          onClick={async () => {
            if (disabled) return;
            const operationId = ++operationIdRef.current;
            const controller = new AbortController();
            abortRef.current = controller;
            setOperation({ operationId, phase: 'starting' });
            setError(null);
            try {
              await exportStaticMap(project, { format, dpi }, entitlement, {
                mapLogicalPixelsPerMm,
                signal: controller.signal,
                onStage: (stage) => {
                  if (operationId !== operationIdRef.current) return;
                  setOperation({ operationId, phase: 'running', stage });
                },
              });
              if (operationId !== operationIdRef.current) return;
              onClose();
            } catch (cause) {
              if (operationId !== operationIdRef.current) return;
              if (cause instanceof OperationCancelledError) {
                setOperation({ operationId, phase: 'complete' });
                return;
              }
              setError(
                cause instanceof StaticExportError
                  ? cause.message
                  : 'Could not prepare the static map export.',
              );
              setOperation({ operationId, phase: 'failed' });
            } finally {
              if (operationId === operationIdRef.current) abortRef.current = null;
            }
          }}
          type="button"
        >
          {preparing ? 'Exporting…' : 'Export'}
        </button>
      </div>
    </DialogShell>
  );
}
